import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, Loader2, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [tierLabel, setTierLabel] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    let polls = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/api/payments/status/${sessionId}`);
        setTierLabel(data.tier_label || "");
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) { /* keep trying */ }
      polls += 1;
      if (polls >= MAX_POLLS) { setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="bg-[#F4EFE6] text-[#2A2825] min-h-[80vh] flex items-center">
      <section className="max-w-2xl mx-auto px-6 md:px-12 py-24 text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-10 h-10 text-[#A9724E] animate-spin mx-auto mb-6" strokeWidth={1.5} />
            <p className="font-serif-display text-2xl font-light">Confirming your membership…</p>
          </div>
        )}
        {state === "paid" && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="payment-paid">
            <div className="w-16 h-16 rounded-full bg-[#A9724E] text-[#F4EFE6] flex items-center justify-center mx-auto mb-7"><Check className="w-8 h-8" strokeWidth={2} /></div>
            <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-4">Welcome to the Club</p>
            <h1 className="font-serif-display font-light text-4xl sm:text-5xl leading-tight mb-5">You're in{tierLabel ? ` — ${tierLabel}` : ""}.</h1>
            <p className="text-[#5E4B3C] leading-relaxed font-body text-lg mb-8">Your membership is active. We just sent a welcome note to your inbox, and you'll be the first to hear about our local gatherings and online activities.</p>
            <Link to="/" className="group inline-flex items-center gap-2 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-8 py-3.5 text-sm tracking-wide transition-colors duration-500">Back to Outpoured <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.5} /></Link>
          </motion.div>
        )}
        {state === "pending" && (
          <div data-testid="payment-pending">
            <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-4">Almost there</p>
            <h1 className="font-serif-display font-light text-3xl sm:text-4xl leading-tight mb-5">Your payment is processing.</h1>
            <p className="text-[#5E4B3C] leading-relaxed font-body text-lg mb-8">This can take a moment. We'll email your welcome as soon as it clears. You can safely close this page.</p>
            <Link to="/" className="inline-flex items-center gap-2 rounded-full bg-[#2A2825] hover:bg-[#413d38] text-[#F4EFE6] px-8 py-3.5 text-sm tracking-wide transition-colors">Back to Outpoured</Link>
          </div>
        )}
        {state === "error" && (
          <div data-testid="payment-error">
            <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-4">Hmm</p>
            <h1 className="font-serif-display font-light text-3xl sm:text-4xl leading-tight mb-5">We couldn't confirm this payment.</h1>
            <p className="text-[#5E4B3C] leading-relaxed font-body text-lg mb-8">If you were charged, don't worry — email <a href="mailto:dej@outpouredliving.com" className="text-[#A9724E] underline underline-offset-4">dej@outpouredliving.com</a> and we'll sort it out right away.</p>
            <Link to="/" className="inline-flex items-center gap-2 rounded-full bg-[#2A2825] hover:bg-[#413d38] text-[#F4EFE6] px-8 py-3.5 text-sm tracking-wide transition-colors">Back to Outpoured</Link>
          </div>
        )}
      </section>
    </div>
  );
}
